import { useMemo, useCallback } from "react"

import { merge } from "lodash"

import {
    Layer,
    MapStyles,
    LINE_LAYER_SIZE,
    SELECTED_LINE_LAYER_SIZE,
    getLineLayerConfig,
    getInterpolatedLineLayerWidth,
    getInterpolatedSelectableLineLayerWidth,
    getSelectableLineSortKey,
    REFERENTIAL_SOURCE,
    REFERENTIAL_LAYER,
    REFERENTIAL_LAYER_CLICKABLE,
    useOrderingLayers,
    BASE_MAP_ID,
} from "@l2r-front/l2r-map"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { useTheme } from "@l2r-front/l2r-ui"
import { selectLayerColorOpacity } from "@l2r-front/l2r-utils"

import { NetworkReferentialSource } from "../NetworkReferentialSource"

export function BaseNetworkReferentialLayer(props) {
    const {
        onClickOnFeature,
        mapId,
        mapStyle,
        roadToHide,
        selectedRoad,
        selectedRoadColor,
        ...layerProps
    } = props

    const { interactive = true } = layerProps

    const theme = useTheme()

    useOrderingLayers([REFERENTIAL_LAYER_CLICKABLE, REFERENTIAL_LAYER], mapId)

    const referentialLayerConfig = useMemo(() => {

        const lineColor = mapStyle === MapStyles.plan ? selectLayerColorOpacity(theme.palette.map.darkReferential, 0.5, selectedRoad, selectedRoadColor) : selectLayerColorOpacity(theme.palette.map.lightReferential, 0.85, selectedRoad, selectedRoadColor)
        return merge(
            getLineLayerConfig({
                paint: {
                    "line-color": lineColor,
                    "line-width": getInterpolatedLineLayerWidth(LINE_LAYER_SIZE),
                },
            }),
            {
                paint: {
                    "line-color": lineColor,
                    "line-width": getInterpolatedSelectableLineLayerWidth(selectedRoad),
                },
                layout: {
                    "line-sort-key": getSelectableLineSortKey(selectedRoad),
                },
                filter: ["!=", ["get", "road", ["at", 0, ["get", "linearLocation"]]], roadToHide || null],
            },
        )
    }, [mapStyle, selectedRoad, selectedRoadColor, theme.palette.map.darkReferential, theme.palette.map.lightReferential, roadToHide])

    const referentialLayerClickConfig = useMemo(() => {
        return getLineLayerConfig({
            "paint": {
                "line-color": "transparent",
                "line-width": getInterpolatedLineLayerWidth(SELECTED_LINE_LAYER_SIZE),
            },
        })
    }, [])

    const handleLayerClick = useCallback(async (event) => {
        const feature = event?.feature
        if (!feature) {
            return
        } else {
            if (onClickOnFeature) {
                onClickOnFeature(feature)
            }
        }
    }, [onClickOnFeature])

    return (
        <NetworkReferentialSource id={REFERENTIAL_SOURCE}>
            <Layer
                {...referentialLayerConfig}
                id={REFERENTIAL_LAYER}
                {...layerProps}
            />
            {interactive && <Layer
                {...referentialLayerClickConfig}
                id={REFERENTIAL_LAYER_CLICKABLE}
                onClick={handleLayerClick}
                {...layerProps}
            />
            }
        </NetworkReferentialSource>
    )
}

BaseNetworkReferentialLayer.propTypes = {
    onClickOnFeature: PropTypes.func,
    mapId: PropTypes.string,
    mapStyle: PropTypes.object,
    roadToHide: PropTypes.string,
    selectedRoad: PropTypes.string,
    selectedRoadColor: PropTypes.string,
}

BaseNetworkReferentialLayer.defaultProps = {
    mapId: BASE_MAP_ID,
}